import { startCase } from "lodash";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useSetRecoilState } from "recoil";

import { SVG_MUI } from "@ryde/assets";
import {
  IRideDto,
  isRideGreyed,
  rideSelectedPassengersIdsAF,
  rideStationsCollapsedIdsAF,
  unique,
  useIsMobile,
} from "@ryde/shared";

import { CheckboxMui } from "../../../../controls";
import { RideStationsHeaderStyled } from "./ride-stations.styled";

interface IRideStationHeaderProps {
  rideDto: Partial<IRideDto>;
  isDragging?: boolean;
  allStationsCollapsed: boolean;
}

export const RideStationsHeader: FC<IRideStationHeaderProps> = ({
  isDragging,
  rideDto,
  allStationsCollapsed,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useIsMobile();
  const isGreyed = isRideGreyed(rideDto);
  const setStationsCollapsedIds = useSetRecoilState(
    rideStationsCollapsedIdsAF(rideDto.id || 0),
  );

  const expandStations = useEvent(() => {
    setStationsCollapsedIds([]);
  });

  const collapseStations = useEvent(() => {
    setStationsCollapsedIds(rideDto?.metadata?.stationIds || []);
  });

  const [selectedPassIds, setSelectedPassIds] = useRecoilState(
    rideSelectedPassengersIdsAF(rideDto?.id || 0),
  );

  const allPassengersIds = useMemo(
    () => rideDto.metadata?.passengersInfo?.map(x => x.id) || [],
    [rideDto.metadata?.passengersInfo],
  );

  const allPassengersSelected = useMemo(() => {
    if (!allPassengersIds.length) {
      return false;
    }

    return allPassengersIds.every(p => selectedPassIds.some(s => s === p));
  }, [allPassengersIds, selectedPassIds]);

  const toggleAllPassengersSelectedState = useEvent(() => {
    if (allPassengersSelected) {
      const allPassengersIdsSet = new Set(allPassengersIds);
      setSelectedPassIds?.(ids => ids.filter(p => !allPassengersIdsSet.has(p)));
    } else {
      setSelectedPassIds?.(ids => unique([...ids, ...allPassengersIds]));
    }
  });

  return (
    <RideStationsHeaderStyled $isDragging={isDragging}>
      {!isMobile && <div className="column_grip_h" aria-hidden />}
      <div className="column_checkbox_h">
        {!isGreyed && (
          <CheckboxMui
            width={15}
            height={15}
            checked={allPassengersSelected}
            onCheckboxToggle={toggleAllPassengersSelectedState}
          />
        )}
      </div>
      {!isMobile && <div className="column_passengers_h" aria-hidden />}
      <div className="column_station_info_h">
        <div className="column_station_header_spacer" aria-hidden />
        <div className="column_station_header">
          {startCase(t("station.single"))}
        </div>
        <div className="column_time_header">{startCase(t("general.TIME"))}</div>
      </div>
      {!isMobile && (
        <div className="column_area_header">{startCase(t("area.single"))}</div>
      )}
      {!isMobile && <div className="column_stop_h" aria-hidden />}
      <div className="column_duration_header">
        {startCase(t("general.DURATION"))}
      </div>
      {!isMobile && (
        <div className="column_toggle_h">
          {allStationsCollapsed && (
            <SVG_MUI.ArrowStatExpand3SVG onClick={expandStations} />
          )}
          {!allStationsCollapsed && (
            <SVG_MUI.ArrowStatCollapse3SVG onClick={collapseStations} />
          )}
        </div>
      )}
    </RideStationsHeaderStyled>
  );
};
